/* eslint-disable react/jsx-no-bind */
import {
  Conversation,
  StripeAssistantLiveAnswersResponse,
  InlineDataConversationMessage,
} from '@stripe-internal/conversations-client';
import {InlineDataProps} from '@stripe-internal/inline-data';
import {LiveAnswersResponse} from './LiveAnswersResponse';

type Props = {
  conversation: Conversation;
  message: InlineDataConversationMessage;
};

export const LiveAnswersResponseContainer = ({
  conversation,
  message,
}: Props) => {
  function onError() {
    if (!conversation.isTabMostRecent()) {
      return;
    }

    conversation.action({
      name: 'inline-data-result',
      message,
      result: {type: 'error'},
    });
  }

  function onComplete(
    content: string,
    answerType: 'unanswerable' | 'answerable' = 'answerable',
  ) {
    if (!conversation.isTabMostRecent()) {
      return;
    }

    // Update the message. If an error happened, the message will the saved with
    // `isDone` as true but without any content. This is consistent with how it
    // currently renders: it will render nothing and the backend has sent a new
    // message to the user about the error which is rendered separately
    const updatedData = {
      ...(message.inlineData.data as StripeAssistantLiveAnswersResponse),
      is_streamed: true,
      content,
    };
    const updatedMessage = {
      ...message,
      inlineData: {...message.inlineData, data: updatedData},
    };

    conversation.action({
      name: 'inline-data-result',
      message: updatedMessage,
      result: {type: 'success', data: {outcome: answerType}},
    });
  }

  const inlineData = message.inlineData as InlineDataProps;
  const data = inlineData.data as unknown as StripeAssistantLiveAnswersResponse;

  let cachedContent = null;
  if (data.is_streamed) {
    cachedContent = data.content;
  } else if (inlineData.isDone) {
    // This is an error state. For the inline data to be marked done but the
    // data is not completed streaming, that means something went wrong. In
    // the future we can make this clearer
    cachedContent = '';
  }

  return (
    <LiveAnswersResponse
      conversation={conversation}
      messageId={message.id}
      llmConversationId={data.llm_conversation_id}
      cacheUuid={data.cache_uuid}
      sources={data.sources}
      cachedContent={cachedContent}
      onComplete={onComplete}
      onError={onError}
    />
  );
};
